// filepath: /Users/reizhafajrian/Documents/GitHub/komisi-admin/src/views/admin/product-digital/index.jsx
import {
  Box,
  Button,
  HStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Flex,
  Input,
  FormLabel,
  FormControl,
  FormErrorMessage,
  Text,
  IconButton,
  VStack,
  Badge,
  Switch,
  Tooltip,
  NumberInput,
  NumberInputField,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';
import { AiOutlinePlus } from 'react-icons/ai';
import { BsFillTrashFill } from 'react-icons/bs';
import { NumericFormat } from 'react-number-format';
import { z } from 'zod';
import Form from 'components/form';
import FormInput from 'components/form/Input';
import { Api } from 'lib/api';
import formatRupiah from 'lib/formatRupiah';
import useStore from 'store/useStore';
import MyDigitalProducts from './components/MyDigitalProducts';

// Define validation schema for digital products
const productDigitalSchema = z.object({
  title: z.string().nonempty('Title is required'),
  description: z.string().optional(),
  price: z
    .string()
    .regex(/^\d+$/)
    .refine((value) => parseFloat(value) >= 0, {
      message: 'Price must number',
    }),
  category: z.string().nonempty('Category is required'),
  thumbnail: z.string().nonempty('Thumbnail URL is required'),
  isActive: z.boolean().default(true),
  tags: z.array(z.string()).optional(),
  content: z
    .array(
      z.object({
        title: z.string().nonempty('Content title is required'),
        description: z.string().optional(),
        youtubeUrl: z
          .string()
          .url('Invalid URL')
          .refine((url) => /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.*/.test(url), {
            message: 'Invalid YouTube URL format',
          }),
        isPrivate: z.boolean().default(false),
        duration: z.number().optional(),
        order: z.number().optional(),
      }),
    )
    .min(1, 'At least one content item is required'),
});

const ProductDigital = () => {
  const [tagInput, setTagInput] = useState('');

  const form = useForm({
    resolver: zodResolver(productDigitalSchema),
    defaultValues: {
      title: '',
      description: '',
      price: '',
      category: '',
      thumbnail: '',
      isActive: true,
      tags: [],
      content: [
        {
          title: '',
          description: '',
          youtubeUrl: '',
          isPrivate: false,
          duration: 15,
          order: 0,
        },
      ],
    },
  });

  const {
    formState: { errors },
    watch,
    setValue,
    control,
  } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'content',
  });

  // Store actions - Replace with actual API calls for digital products
  const [categoryList, getCategoryHandler] = useStore((s) => [s.categoryList, s.getCategory]);

  const { myStore } = useStore();

  useEffect(() => {
    myStore();
    getCategoryHandler({
      page: 1,
      limit: 10,
      type: 'digital',
    });
  }, []);

  const mappingCategoryList = categoryList?.data?.map((result) => ({
    label: result.name,
    ...result,
  }));

  console.log(errors);

  const onSubmit = async (values) => {
    try {
      // This is a mock function that simulates saving digital products

      const response = await Api.post({
        url: '/admin/digital-products/create',
        data: values,
      });

      if (response.status === 200) {
        toast.success('Digital product created successfully!');
      }

      // Reset the form after successful submission
      form.reset({
        title: 'Sample Digital Product',
        description: 'This is a sample digital product description.',
        price: '99000',
        category: 'digital',
        thumbnail: 'https://via.placeholder.com/300',
        isActive: true,
        tags: ['course', 'digital', 'sample'],
        content: [
          {
            title: 'Introduction to the course',
            description: 'Welcome to the course! This is the first lesson.',
            youtubeUrl: 'https://youtube.com/watch?v=sample',
            isPrivate: false,
            duration: 15,
            order: 0,
          },
        ],
      });
      // Scroll to top of the form
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Alternative to refresh - just show success message
      // history.go(0);
    } catch (error) {
      console.error(error);
    }
  };

  const addTag = () => {
    if (tagInput.trim()) {
      const currentTags = watch('tags') || [];
      if (!currentTags.includes(tagInput.trim())) {
        setValue('tags', [...currentTags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    const currentTags = watch('tags') || [];
    setValue(
      'tags',
      currentTags.filter((tag) => tag !== tagToRemove),
    );
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} backgroundColor={'white'}>
      <Tabs isLazy>
        <TabList>
          <Tab>Create Digital Product</Tab>
          <Tab>My Digital Products</Tab>
        </TabList>
        <TabPanels>
          <TabPanel background={'#f3f5fe'}>
            <Box gap="20px" mb="20px">
              <Form methods={form} onSubmit={onSubmit}>
                <Flex borderRadius={'2xl'} px={10} py={10} flexDirection={'column'} backgroundColor={'white'}>
                  <Text mb={4} fontSize="xl" fontWeight="bold">
                    Basic Information
                  </Text>

                  <FormInput
                    propsFormControl={{ mb: 4 }}
                    label="Product Title"
                    inputProps={{
                      ...form.register('title'),
                      name: 'title',
                      placeholder: 'Enter product title',
                    }}
                    errors={errors}
                    type="TEXT"
                  />

                  <FormInput
                    propsFormControl={{ mb: 4 }}
                    label="Description"
                    inputProps={{
                      ...form.register('description'),
                      name: 'description',
                      placeholder: 'Enter product description',
                    }}
                    errors={errors}
                    type="TEXTAREA"
                  />

                  <HStack mb={4} justifyContent={'space-between'} alignItems={'flex-start'}>
                    <FormControl flex="1" isInvalid={errors?.price}>
                      <FormLabel htmlFor="price">Price</FormLabel>
                      <NumericFormat
                        {...form.register('price')}
                        placeholder={formatRupiah('50000')}
                        style={{ marginTop: 3 }}
                        className="input-custom"
                        valueIsNumericString={true}
                        prefix="Rp. "
                        onValueChange={({ floatValue }) => {
                          setValue('price', String(floatValue) || 0);
                        }}
                        thousandsGroupStyle="thousand"
                        thousandSeparator=","
                      />
                      <FormErrorMessage>{errors?.price && errors?.price?.message}</FormErrorMessage>
                    </FormControl>

                    <FormInput
                      propsFormControl={{
                        mb: 4,
                        flex: '1',
                        onClick: () =>
                          getCategoryHandler({
                            page: 1,
                            limit: 10,
                            type: 'digital',
                          }),
                      }}
                      label="Category"
                      inputProps={{
                        ...form.register('category'),
                        name: 'category',
                        placeholder: 'Select Category',
                        optionsData: mappingCategoryList,
                        onChange: (value) => setValue('category', value?._id),
                      }}
                      type="SELECT"
                      errors={errors}
                    />
                  </HStack>

                  <FormInput
                    propsFormControl={{ mb: 4 }}
                    label="Thumbnail URL"
                    inputProps={{
                      ...form.register('thumbnail'),
                      name: 'thumbnail',
                      placeholder: 'Enter thumbnail image URL',
                    }}
                    errors={errors}
                    type="TEXT"
                  />

                  <FormControl mb={4} display="flex" alignItems="center">
                    <FormLabel htmlFor="isActive" mb="0">
                      Product Active
                    </FormLabel>
                    <Switch
                      id="isActive"
                      {...form.register('isActive')}
                      isChecked={watch('isActive')}
                      onChange={(e) => setValue('isActive', e.target.checked)}
                    />
                  </FormControl>

                  <FormControl mb={4} isInvalid={errors?.tags}>
                    <FormLabel htmlFor="tags">Tags (Press Enter to Add)</FormLabel>
                    <HStack>
                      <Input
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleTagInputKeyDown}
                        placeholder="Add tags..."
                        id="tags"
                      />
                      <Button onClick={addTag} colorScheme="blue">
                        Add
                      </Button>
                    </HStack>
                    <Flex mt={2} flexWrap="wrap" gap={2}>
                      {(watch('tags') || []).map((tag, index) => (
                        <Badge
                          key={index}
                          colorScheme="blue"
                          borderRadius="full"
                          px={2}
                          py={1}
                          display="flex"
                          alignItems="center"
                        >
                          {tag}
                          <Box as="span" ml={1} cursor="pointer" onClick={() => removeTag(tag)}>
                            ×
                          </Box>
                        </Badge>
                      ))}
                    </Flex>
                  </FormControl>
                </Flex>

                <Flex borderRadius={'2xl'} px={10} py={10} mt={5} flexDirection={'column'} backgroundColor={'white'}>
                  <Flex justifyContent="space-between" alignItems="center" mb={4}>
                    <Text fontSize="xl" fontWeight="bold">
                      Content Items
                    </Text>
                    <Button
                      leftIcon={<AiOutlinePlus />}
                      colorScheme="blue"
                      size="sm"
                      onClick={() =>
                        append({
                          title: '',
                          description: '',
                          youtubeUrl: '',
                          isPrivate: false,
                          duration: 0,
                          order: fields.length,
                        })
                      }
                    >
                      Add Content
                    </Button>
                  </Flex>

                  {fields.map((field, index) => (
                    <Box key={field.id} p={4} mb={4} borderWidth="1px" borderRadius="md" position="relative">
                      <Flex justifyContent="space-between" alignItems="center" mb={2}>
                        <Text fontWeight="medium">Item #{index + 1}</Text>
                        <IconButton
                          icon={<BsFillTrashFill />}
                          colorScheme="red"
                          size="sm"
                          isDisabled={fields.length === 1}
                          onClick={() => remove(index)}
                          aria-label="Remove content item"
                        />
                      </Flex>

                      <VStack spacing={4} align="stretch">
                        <FormInput
                          propsFormControl={{ mb: 2 }}
                          label="Title"
                          inputProps={{
                            ...form.register(`content.${index}.title`),
                            name: `content.${index}.title`,
                            placeholder: 'Content title',
                          }}
                          errors={errors?.content?.[index]}
                          type="TEXT"
                        />

                        <FormInput
                          propsFormControl={{ mb: 2 }}
                          label="Description"
                          inputProps={{
                            ...form.register(`content.${index}.description`),
                            name: `content.${index}.description`,
                            placeholder: 'Content description',
                          }}
                          errors={errors?.content?.[index]}
                          type="TEXTAREA"
                        />

                        <FormInput
                          propsFormControl={{ mb: 2 }}
                          label="YouTube URL"
                          inputProps={{
                            ...form.register(`content.${index}.youtubeUrl`),
                            name: `content.${index}.youtubeUrl`,
                            placeholder: 'https://youtube.com/...',
                          }}
                          errors={errors?.content?.[index]}
                          type="TEXT"
                        />

                        <HStack spacing={8}>
                          <FormControl display="flex" alignItems="center">
                            <FormLabel htmlFor={`content.${index}.isPrivate`} mb="0">
                              Private Content
                            </FormLabel>
                            <Tooltip label="Mark as private if this content is premium">
                              <Switch
                                id={`content.${index}.isPrivate`}
                                {...form.register(`content.${index}.isPrivate`)}
                                isChecked={watch(`content.${index}.isPrivate`)}
                                onChange={(e) => setValue(`content.${index}.isPrivate`, e.target.checked)}
                              />
                            </Tooltip>
                          </FormControl>

                          <FormControl>
                            <FormLabel htmlFor={`content.${index}.duration`}>Duration (minutes)</FormLabel>
                            <NumberInput min={0}>
                              <NumberInputField
                                id={`content.${index}.duration`}
                                {...form.register(`content.${index}.duration`, {
                                  valueAsNumber: true,
                                })}
                                placeholder="Duration in minutes"
                              />
                            </NumberInput>
                          </FormControl>

                          <FormControl>
                            <FormLabel htmlFor={`content.${index}.order`}>Display Order</FormLabel>
                            <NumberInput min={0}>
                              <NumberInputField
                                id={`content.${index}.order`}
                                {...form.register(`content.${index}.order`, {
                                  valueAsNumber: true,
                                })}
                                placeholder="Order number"
                              />
                            </NumberInput>
                          </FormControl>
                        </HStack>
                      </VStack>
                    </Box>
                  ))}
                  {errors?.content?.message && (
                    <Text color="red.500" mb={4}>
                      {errors.content.message}
                    </Text>
                  )}
                </Flex>

                <Button mt={6} colorScheme="blue" type="submit">
                  Save Digital Product
                </Button>
              </Form>
            </Box>
          </TabPanel>
          <TabPanel>
            <MyDigitalProducts />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default ProductDigital;
